// 异常售后的状态文案与颜色（文档 18 §4 收敛规则：5 色 + 灰，一行只一个实底标签）。
// - 状态（待处理 / 处理中 / 已处理 / 已关闭）是异常的主状态 → 实底标签，与订单页「异常」页签同色；
// - 类型（破损 / 少发 / 补发 / 退货）是次级信息 → 色点 + 文字（TypeMark），不再用第二个实底标签；
// - 责任方、处理结果、库存影响 → 纯文字 / 色点。
// 类型、状态的色值与 lib/status.ts 同源，保证订单详情与异常模块看到的是同一种颜色。
import type { TagTone } from '@/lib/status';
import { exceptionStatus as baseStatus, exceptionType as baseType } from '@/lib/status';
import { qty } from '@/lib/format';
import type { ExceptionBase } from './types';

export type Tone = TagTone;

/** 破损 red（损失）· 少发 amber（等人动手）· 补发 blue（进行中）· 退货 orange（有未了事项） */
export const excType: Record<string, { label: string; tone: TagTone }> = baseType;
/** 待处理 amber · 处理中 blue · 已处理 green · 已关闭 灰 */
export const excStatus: Record<string, { label: string; tone: TagTone }> = baseStatus;

export const typeOf = (t: string) => excType[t] || { label: t, tone: 'none' as TagTone };
export const statusOf = (t: string) => excStatus[t] || { label: t, tone: 'none' as TagTone };

export const isOpen = (e: Pick<ExceptionBase, 'status'>) => e.status === 'PENDING' || e.status === 'PROCESSING';

/** 处理结果（原系统 exceptionResolutionLabels） */
export const resolution: Record<string, string> = {
  NOTED: '已备注',
  RESHIPPED: '已补发',
  REFUNDED: '已退款',
  IGNORED: '已忽略',
  TRANSFERRED: '已转采购/仓库处理',
};

/** 责任方（原系统 exceptionResponsibilityLabels） */
export const responsibility: Record<string, string> = {
  CUSTOMER: '客户',
  WAREHOUSE: '仓库',
  LOGISTICS: '物流',
  SUPPLIER: '供应商',
  OTHER: '其它',
};

/** 库存影响（原系统 getStockImpact）：登记异常本身不动库存，补发 / 退货入库时才动 */
export function stockImpact(e: Pick<ExceptionBase, 'type'>): { label: string; dot: 'green' | 'amber' | 'grey' } {
  if (e.type === 'RETURNED') return { label: '退货入库增加库存', dot: 'green' };
  if (e.type === 'SHORT_SHIPPED' || e.type === 'DAMAGED' || e.type === 'RESHIPMENT') return { label: '补发时减少库存', dot: 'amber' };
  return { label: '仅记录不动库存', dot: 'grey' };
}

/** 推荐处理（原系统 getRecommendedAction） */
export function recommended(e: Pick<ExceptionBase, 'type' | 'reshipmentShipment' | 'returnInboundQuantity'> & { canOperate?: boolean }) {
  if (e.canOperate === false) return '未出库，暂不可处理';
  if (e.reshipmentShipment) return `已生成补发 ${e.reshipmentShipment.shipmentNo}`;
  if (Number(e.returnInboundQuantity || 0) > 0) return `已退货入库 ${qty(e.returnInboundQuantity)} 个`;
  if (e.type === 'RETURNED') return '退货入库';
  if (e.type === 'SHORT_SHIPPED') return '补发或关闭';
  if (e.type === 'DAMAGED') return '补发 / 仅记录';
  return '补发';
}

/** 状态视图 = 老接口的 status 参数（open = 待处理 + 处理中，老版默认视图） */
export const VIEWS: { key: string; label: string; status: string; count: (s: { open: number; pending: number; processing: number; resolved: number; closed: number; all: number }) => number }[] = [
  { key: 'open', label: '未闭环', status: 'open', count: (s) => s.open },
  { key: 'pending', label: '待处理', status: 'PENDING', count: (s) => s.pending },
  { key: 'processing', label: '处理中', status: 'PROCESSING', count: (s) => s.processing },
  { key: 'resolved', label: '已处理', status: 'RESOLVED', count: (s) => s.resolved },
  { key: 'closed', label: '已关闭', status: 'CLOSED', count: (s) => s.closed },
  { key: 'all', label: '全部异常', status: 'all', count: (s) => s.all },
];

/** 「筛选」里的三组参数，与 buildExceptionWhere 一一对应 */
export const FILTERS: { key: 'type' | 'responsibility' | 'stockImpact'; title: string; opts: { value: string; label: string }[] }[] = [
  { key: 'type', title: '异常类型', opts: Object.entries(excType).map(([value, v]) => ({ value, label: v.label })) },
  { key: 'responsibility', title: '责任方', opts: Object.entries(responsibility).map(([value, label]) => ({ value, label })) },
  {
    key: 'stockImpact',
    title: '库存影响',
    opts: [
      { value: 'out', label: '会减少库存' },
      { value: 'in', label: '会增加库存' },
      { value: 'none', label: '不调整库存' },
    ],
  },
];
